import { useState, useRef, useEffect } from "react";
import dayjs from "dayjs";

import usePlatformLayoutContext from "../contexts/platformLayout";
import { fetchOurApi } from "../utils/apiCalls";
import { useCollectionDataListener, useCachedFirebaseCrud } from "../firebase";
import { analyticsTrack } from "../analytics";
import { getAccountId } from "../accountHelpers";

const requestURI = async () => {
  const companyId = getAccountId();
  return await fetchOurApi({
    path: `/oauth/quickbooks/requestURI/${companyId}`,
    method: "GET",
    callback: (URI) => URI
  });
};

// requestURI resolves to a bare authorize-URL string on success, but a failed
// fetch (network error, or a caught backend error) resolves to a plain
// { error, message } object instead - there's no shared success/failure shape
// to check like triggerManualSync has. Without this guard, window.open coerces
// that object to the string "[object Object]", which the browser treats as a
// relative path off the current page and opens our own app in the popup
// instead of QuickBooks' login screen.
export const isValidQuickbooksUri = (uri) =>
  typeof uri === "string" &&
  (uri.startsWith("http://") || uri.startsWith("https://"));

const triggerManualSync = async () => {
  const companyId = getAccountId();
  return await fetchOurApi({
    path: `/oauth/quickbooks/sync/${companyId}`,
    method: "POST",
    callback: (response) => response
  });
};

export const useQuickbooks = (props = {}) => {
  const { setOpen, setQuickbooksError } = props;
  const [quickbooksTokens, tokenLoading] = useCollectionDataListener(
    "integrations",
    [["name", "==", "quickbooks"]]
  );

  const [quickbooksTasks, tasksLoading] = useCollectionDataListener("tasks", [
    ["source", "==", "quickbooks"]
  ]);

  const { deleteCachedCollectionDoc } = useCachedFirebaseCrud();

  const quickbooksToken =
    quickbooksTokens.find(({ archived }) => !archived) || {};

  const {
    pullDate,
    id: tokenDocId,
    refreshTokenExpiration,
    lastTaskId,
    archived,
    lastTransactionAt,
    transactionCount,
    id
  } = quickbooksToken;

  const currentTask =
    quickbooksTasks?.find((task) => task.id === lastTaskId) ||
    quickbooksTasks?.sort(
      (a, b) => new Date(b.startTime) - new Date(a.startTime)
    )[0];

  const taskStatus = currentTask ? currentTask.status : "finished";

  const { activateSnackbar } = usePlatformLayoutContext() || {};
  const windowRef = useRef();
  const [isWindowOpen, setIsWindowOpen] = useState(false);

  const handleLaunchError = (message) => {
    if (!setQuickbooksError) {
      return;
    }
    setQuickbooksError(
      message ||
        "We couldn't connect to QuickBooks. Please try again in a moment."
    );
  };

  const launchQuickbooks = async () => {
    if (setQuickbooksError) {
      setQuickbooksError("");
    }

    analyticsTrack("Quickbooks Synced");
    const URI = await requestURI();

    if (!isValidQuickbooksUri(URI)) {
      const { message } = URI || {};
      return handleLaunchError(message);
    }

    windowRef.current = window.open(URI, "quickbooks Popup", "toolbars=0");
    if (!windowRef.current && setQuickbooksError) {
      return setQuickbooksError(
        "This popup was blocked by your browser. Please allow popups to connect to QuickBooks."
      );
    }
    return setIsWindowOpen(true);
  };

  const showSyncStartedSnackbar = () => {
    if (!activateSnackbar) {
      return;
    }
    activateSnackbar({
      message:
        "Your transactions are being loaded from QuickBooks. You'll get an email when we're done.",
      alert: "success"
    });
  };

  const handleSyncError = (message) => {
    if (setQuickbooksError) {
      setQuickbooksError(
        message ||
          "We couldn't start a QuickBooks sync. Please try again in a moment."
      );
    }
  };

  const fetchQuickbooksData = async () => {
    if (setQuickbooksError) {
      setQuickbooksError("");
    }

    const response = await triggerManualSync();
    const { isPopup, URI, error, message, success } = response || {};

    if (isPopup) {
      windowRef.current = window.open(URI, "quickbooks Popup", "toolbars=0");
      if (!windowRef.current && setQuickbooksError) {
        return setQuickbooksError(
          "This popup was blocked by your browser. Please allow popups to reconnect to QuickBooks."
        );
      }
      return setIsWindowOpen(true);
    }

    if (error || !success) {
      return handleSyncError(message);
    }

    showSyncStartedSnackbar();
    if (setOpen) {
      setOpen(false);
    }
  };

  const signOutQuickbooks = async () =>
    deleteCachedCollectionDoc("integrations", tokenDocId);

  const lastPull = pullDate && dayjs(pullDate);
  const difference = dayjs().diff(lastPull, "second");

  useEffect(() => {
    const loadingMessage = () => {
      if (
        windowRef.current &&
        difference &&
        difference < 10 &&
        activateSnackbar
      ) {
        activateSnackbar({
          message:
            "Your transactions are being loaded from QuickBooks. You'll get an email when we're done.",
          alert: "success"
        });
        if (setOpen) {
          setOpen(false);
        }
        return setIsWindowOpen(false);
      }
    };
    if (isWindowOpen) {
      loadingMessage();
    }
  }, [difference, isWindowOpen, setOpen, activateSnackbar]);

  return {
    id,
    archived,
    pullDate,
    lastTransactionAt,
    transactionCount,
    taskStatus,
    refreshTokenExpiration,
    tokenLoading: tokenLoading || tasksLoading,
    launchQuickbooks,
    fetchQuickbooksData,
    signOutQuickbooks,
    currentTask // Expose task data for detailed status if needed
  };
};
